import { FirestoreDataConverter } from "@angular/fire/firestore";
import { ExternalProductionState } from "../enums/external-production-state.enum";
import { TimeSlot } from "./time-slot.model";
import { SourceType } from "../enums/source-type.enum";
import { OperationTypeEnum } from "../enums/operation-type.enum";
import { DocumentModel } from "./document.model";
import { InstallationLocation } from "./installation-location.model";

export class ExternalProduction {
  // External production id
  id: string;
  // Order id
  orderId: string;
  // Quotation id
  quotationId: string;
  // Organization id
  organizationId: string;
  // Erp product id
  erpId: string;
  // Data source type
  source: SourceType;
  // Description
  description: string;
  // Subcontractor id
  subcontractorId: string;
  subcontractorEmails: string[];
  // State
  state: ExternalProductionState;
  // Time slots
  timeSlots: TimeSlot[];
  // Information about associated user
  user: ({ id: string, name: string, email: string });
  // Price of the subcontract
  price: number;
  // Type of operation
  operationType: OperationTypeEnum;
  // Ids of linked products
  productIds: string[];
  // Subcontracting invoices
  invoice: DocumentModel;
  // Subcontracting purchase order
  purchaseOrder: DocumentModel;
  // Technical documents
  technicalDocuments: DocumentModel[];
  // Installation locations
  installationLocations: InstallationLocation[];
  subcontractorCanEdit: boolean;
  // List of emails to notify when all installation photos are uploaded
  notifeeEmails: string[];
  // List of emails that have already received the photo completion notification
  notifiedEmails: string[];
  // Token for unauthenticated access to the photo view page
  photoViewToken: string;

  constructor(externalProduction?: Partial<ExternalProduction>) {
    this.id = externalProduction?.id ?? '';
    this.orderId = externalProduction?.orderId ?? '';
    this.quotationId = externalProduction?.quotationId ?? '';
    this.organizationId = externalProduction?.organizationId ?? '';
    this.erpId = externalProduction?.erpId ?? '';
    this.source = externalProduction?.source ?? SourceType.OKTO;
    this.description = externalProduction?.description ?? '';
    this.subcontractorId = externalProduction?.subcontractorId ?? '';
    this.subcontractorEmails = externalProduction?.subcontractorEmails ?? [];
    this.state = externalProduction?.state ?? ExternalProductionState.NEW;
    this.timeSlots = (externalProduction?.timeSlots ?? []).map((timeSlot: any) => ({
      ...timeSlot,
      start: timeSlot.start ? new Date(timeSlot.start) : null,
      end: timeSlot.end ? new Date(timeSlot.end) : null,
    }));
    this.user = externalProduction?.user ?? null;
    this.operationType = externalProduction?.operationType ?? OperationTypeEnum.PRODUCTION;
    this.productIds = externalProduction?.productIds ?? [];
    this.technicalDocuments = externalProduction?.technicalDocuments ?? [];
    this.invoice = externalProduction?.invoice ?? null;
    this.purchaseOrder = externalProduction?.purchaseOrder ?? null;
    this.installationLocations = (externalProduction?.installationLocations ?? [])
      .map(loc => new InstallationLocation(loc));
    this.price = externalProduction?.price ?? 0;
    this.subcontractorCanEdit = externalProduction?.subcontractorCanEdit ?? false;
    this.notifeeEmails = externalProduction?.notifeeEmails ?? [];
    this.notifiedEmails = externalProduction?.notifiedEmails ?? [];
    this.photoViewToken = externalProduction?.photoViewToken ?? '';
  }

  /**
   * Converter used to convert an object for firestore
   */
  static converter: FirestoreDataConverter<ExternalProduction> = {
    toFirestore: (externalProduction: ExternalProduction) => ({
      id: externalProduction.id,
      orderId: externalProduction.orderId,
      quotationId: externalProduction.quotationId,
      organizationId: externalProduction.organizationId,
      erpId: externalProduction.erpId,
      source: externalProduction.source,
      description: externalProduction.description,
      subcontractorId: externalProduction.subcontractorId,
      subcontractorEmails: externalProduction.subcontractorEmails,
      state: externalProduction.state,
      timeSlots: externalProduction.timeSlots,
      user: externalProduction.user,
      operationType: externalProduction.operationType,
      productIds: externalProduction.productIds,
      technicalDocuments: externalProduction.technicalDocuments,
      invoice: externalProduction.invoice,
      purchaseOrder: externalProduction.purchaseOrder,
      installationLocations: externalProduction.installationLocations,
      price: externalProduction.price,
      subcontractorCanEdit: externalProduction.subcontractorCanEdit,
      notifeeEmails: externalProduction.notifeeEmails,
      notifiedEmails: externalProduction.notifiedEmails,
      photoViewToken: externalProduction.photoViewToken,
    }),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new ExternalProduction({
        ...data,
        id: snapshot.id,
        subcontractorEmails: data.subcontractorEmails ?? [],
        timeSlots: (data.timeSlots ?? []).map((timeSlot: any) => ({
          ...timeSlot,
          start: timeSlot.start ? new Date(timeSlot.start.seconds * 1000) : null,
          end: timeSlot.end ? new Date(timeSlot.end.seconds * 1000) : null,
        })),
        installationLocations: (data.installationLocations ?? []).map((loc: any) => new InstallationLocation({
          ...loc,
          date: loc.date ? new Date(loc.date.seconds * 1000) : null,
        })),
      });
    }
  }
}
